export const PROPERTY_MAP = {
  backgroundColor: 'bg',
  fontFamily: 'ff',
  fontSize: 'fs',
  fontColor: 'fc',
  fontWeight: 'fw',
  fontStyle: 'fst',
  textDecoration: 'td',
  textAlign: 'ta',
  verticalAlign: 'va',
  lineHeight: 'lh',
  paddingTop: 'pt',
  paddingRight: 'pr',
  paddingBottom: 'pb',
  paddingLeft: 'pl',
  borderTop: 'bt',
  borderRight: 'br',
  borderBottom: 'bb',
  borderLeft: 'bl',
};

export const borderMap = { width: 'w', color: 'c' };

function isBorderProperty(propertyName) {
  return (
    propertyName === 'borderTop' ||
    propertyName === 'borderRight' ||
    propertyName === 'borderBottom' ||
    propertyName === 'borderLeft'
  );
}

export function getBorderProperty(cell, borderSide) {
  if (!cell) return undefined;

  const compactName = PROPERTY_MAP[borderSide];
  const border = cell[compactName] || cell[borderSide];

  if (!border) return undefined;

  return normalizeBorder(border);
}

function normalizeBorder(border) {
  if (!border) return undefined;

  const width = border.w !== undefined ? border.w : border.width;
  const color = border.c !== undefined ? border.c : border.color;

  if (width === undefined && color === undefined) return undefined;

  return {
    width: width !== undefined ? width : 1,
    color: color !== undefined ? color : '#EEEEEE',
  };
}

export function getCellProperty(cell, propertyName) {
  if (!cell) return undefined;

  const compactName = PROPERTY_MAP[propertyName];
  const compactValue = cell[compactName];

  if (compactName && compactValue !== undefined) {
    if (isBorderProperty(propertyName)) {
      return normalizeBorder(compactValue);
    }

    return compactValue;
  }

  const oldValue = cell[propertyName];

  if (oldValue !== undefined) {
    if (isBorderProperty(propertyName)) {
      return normalizeBorder(oldValue);
    }

    return oldValue;
  }

  return undefined;
}

function compactBorder(border) {
  if (!border) return border;

  const compactBorder = {};

  if (border.width !== undefined) {
    compactBorder.w = border.width;
  }
  if (border.color !== undefined) {
    compactBorder.c = border.color;
  }

  return compactBorder;
}

export function setCellProperty(cell, propertyName, value) {
  if (!cell) return cell;

  const compactName = PROPERTY_MAP[propertyName];

  if (!compactName) {
    cell[propertyName] = value;

    return cell;
  }

  let valueToSet = value;

  if (isBorderProperty(propertyName) && value && typeof value === 'object') {
    valueToSet = compactBorder(value);
  }

  cell[compactName] = valueToSet;

  // Remove old verbose name if it exists (cleanup)
  if (cell[propertyName] !== undefined) {
    delete cell[propertyName];
  }

  return cell;
}

export function createCellFromSource(sourceCell, overrides = {}) {
  const newCell = {
    id: overrides.id || Date.now(),
    content: overrides.content || '',
  };

  // Copy styling properties using compact names
  const fs = getCellProperty(sourceCell, 'fontSize');
  const ff = getCellProperty(sourceCell, 'fontFamily');
  const fc = getCellProperty(sourceCell, 'fontColor');
  const bg = getCellProperty(sourceCell, 'backgroundColor');
  const ta = getCellProperty(sourceCell, 'textAlign');
  const va = getCellProperty(sourceCell, 'verticalAlign');
  const pt = getCellProperty(sourceCell, 'paddingTop');
  const pr = getCellProperty(sourceCell, 'paddingRight');
  const pb = getCellProperty(sourceCell, 'paddingBottom');
  const pl = getCellProperty(sourceCell, 'paddingLeft');
  const bt = getBorderProperty(sourceCell, 'borderTop');
  const br = getBorderProperty(sourceCell, 'borderRight');
  const bb = getBorderProperty(sourceCell, 'borderBottom');
  const bl = getBorderProperty(sourceCell, 'borderLeft');
  const lh = getCellProperty(sourceCell, 'lineHeight');

  // Set compact properties with defaults
  if (fs !== undefined) newCell.fs = fs;
  if (ff !== undefined) newCell.ff = ff;
  if (fc !== undefined) newCell.fc = fc;
  if (bg !== undefined) newCell.bg = bg;
  if (ta !== undefined) newCell.ta = ta;
  if (va !== undefined) newCell.va = va;
  if (pt !== undefined) newCell.pt = pt;
  if (pr !== undefined) newCell.pr = pr;
  if (pb !== undefined) newCell.pb = pb;
  if (pl !== undefined) newCell.pl = pl;
  if (lh !== undefined) newCell.lh = lh;

  // Copy borders with compact format
  if (bt) newCell.bt = { w: bt.width, c: bt.color };
  if (br) newCell.br = { w: br.width, c: br.color };
  if (bb) newCell.bb = { w: bb.width, c: bb.color };
  if (bl) newCell.bl = { w: bl.width, c: bl.color };

  return newCell;
}
